import { Checkbox, Grid, Avatar, Typography } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { QueryKeyConfig } from '../../api/api-helper';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { LoadingIndicator } from '../loading-indicator.component';
import { useEffect, useState } from 'react';

interface Props {
  name: string;
  label: string;
  email: string;
  phone: string;
  valueKey: string;
  dataItems?: any[];
  isParentLoading?: boolean;
  getQueryKeyConfig?: (inputData: any, getValues: any) => QueryKeyConfig;
  watchKeys?: string[];
}

const mergeDataItems = (data1: any[], data2: any[], key: string) => {
  const arr1 = Array.isArray(data1) ? data1 : [];
  const arr2 = Array.isArray(data2) ? data2 : [];

  const filtered = arr1.filter(
    (d) => !arr2.some((d2) => d2[key] === d[key])
  );

  return [...filtered, ...arr2];
};

const getPastelColor = (text: string) => {
  let hash = 0;

  for (let i = 0; i < text?.length; i++) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash);
  }

  const h = hash % 360;
  const s = 60 + (hash % 20);
  const l = 75 + (hash % 10);

  return `hsl(${h}, ${s}%, ${l}%)`;
};

const getInitials = (name: string) => {
  if (!name) return '';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

export const FormMultiCheckboxAvatar = ({
  name,
  label,
  email,
  phone,
  valueKey,
  dataItems,
  isParentLoading,
  getQueryKeyConfig,
  watchKeys,
}: Props) => {
  const { control, setValue, getValues } = useFormContext();

  const [completeData, setCompleteData] = useState<any[]>([]);

  // watch dependent fields
  useWatch({
    name: watchKeys || [],
  });

  const watchValues = watchKeys?.reduce(
    (acc: any, key) => ({ ...acc, [key]: getValues(key) }),
    {}
  );

  const query = getQueryKeyConfig?.(watchValues, getValues);

  const { data, isLoading, isFetching } =
    useLoadData<ApiResponse<any[]>>(query, !!query);

  useEffect(() => {
    const merged = mergeDataItems(
      data?.data ?? [],
      dataItems ?? [],
      valueKey
    );

    setCompleteData(merged);

    // ✅ extract IDs where isFastFieldCM = true
    const preSelectedIds = merged
      ?.filter((item) => item?.isFastFieldCM)
      ?.map((item) => item[valueKey]);

    if (preSelectedIds?.length > 0) {
      const currentValues = getValues(name) || [];

      // merge without duplicates
      const updatedValues = Array.from(
        new Set([...currentValues, ...preSelectedIds])
      );

      setValue(name, updatedValues, { shouldValidate: true });
    }
  }, [data, dataItems, valueKey]);

  return (
    <>
      <LoadingIndicator isLoading={isLoading || isFetching || isParentLoading} />

      <Controller
        name={name}
        control={control}
        defaultValue={[]}
        render={({ field: { value, onChange } }) => {
          const safeValue = Array.isArray(value) ? value : [];

          const handleToggle = (id: any) => {
            if (safeValue.includes(id)) {
              onChange(safeValue.filter((v: any) => v !== id));
            } else {
              onChange([...safeValue, id]);
            }
          };

          return (
            <Grid container direction="column" gap={1}>
              {completeData?.length > 0 ? (
                completeData?.map((item: any) => {
                  const itemId = item[valueKey];
                  const checked = safeValue.includes(itemId);

                  const displayName = item[label];
                  const displayEmail = item[email];
                  const displayPhone = item[phone];

                  const avatarProps = {
                    sx: {
                      bgcolor: getPastelColor(displayName),
                      color: '#333',
                    },
                    children: getInitials(displayName),
                  };

                  return (
                    <Grid
                      key={itemId}
                      container
                      alignItems="center"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleToggle(itemId)}
                    >
                      <Checkbox
                        checked={checked}
                        onChange={() => handleToggle(itemId)}
                        onClick={(e) => e.stopPropagation()}
                      />

                      <Avatar
                        {...avatarProps}
                        sx={{ ...avatarProps.sx, ml: 1 }}
                      />

                      <div style={{ paddingLeft: 8 }}>
                        <Typography variant="body1">
                          {displayName}
                        </Typography>

                        <Typography variant="caption" color="#737373">
                          {[displayEmail, displayPhone]
                            .filter(Boolean)
                            .join(' | ')}
                        </Typography>
                      </div>
                    </Grid>
                  );
                })
              ) : (
                !isLoading &&
                !isFetching &&
                !isParentLoading && (
                  <Typography variant="body1">
                    {`No ${name}`}
                  </Typography>
                )
              )}
            </Grid>
          );
        }}
      />
    </>
  );
};